import { useCallback } from 'react';

import { HookFormResolver, Text } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { useTranslation } from 'react-i18next';

import { useTranslateListAll } from '../../../../components/translate/useTranslateList';
import { factoryYup } from '../../../../core/factory-yup';
import { getTaxpayerByNit, searchTaxpayer } from '../../https';
import { type Taxpayers } from '../../TaxpayerRequest/types';

import { AutoComplete, debounce } from './AutoComplete';
import { LexicalEditorComponent } from './LexicalEditorComponent';

const yup = factoryYup();
const validations = yup.object().shape({
  search: yup.object().nullable().required(),
  taxpayerName: yup.string().required(),
  content: yup.string().min(30).required(),
  subject: yup.string().required()
});

export const FormCommunication = ({
  form,
  onSubmit
}: {
  form: string;
  onSubmit: (values: any) => void;
}) => {
  const { t } = useTranslation();

  const langList = useTranslateListAll();

  const labelSearch = t('common.label.search');

  const taxpayerNameLabel = t('taxpayer-notification.label.taxpayerName');
  const subjectLabel = t('taxpayer-notification.label.subject');
  const languageLabel = t('administration-user.label.locale');

  const loadOptionsDebounced = useCallback(
    debounce((inputValue: string, callback: (options: any) => void) => {
      if (inputValue?.trim()?.length > 0) {
        searchTaxpayer(inputValue).then((result: Array<Taxpayers>) => {
          const data = result.map((x) => {
            return { value: x.id, label: `${x.tin} - ${x.name}`, tin: x.tin };
          });
          callback(data);
        });
      } else {
        callback([]);
      }
    }, 500),
    []
  );

  if (!(langList?.length > 0)) {
    return <></>;
  }
  return (
    <>
      <HookFormResolver nameForm={form} onSubmit={onSubmit} validations={validations}>
        {({ formState: { errors }, reset, setValue, clearErrors }: HookFormMethods) => {
          return (
            <>
              <Row>
                <Col>
                  <AutoComplete
                    label={labelSearch}
                    name={'search'}
                    whenChange={async (value: any) => {
                      const taxpayer =
                        value == undefined ? undefined : await getTaxpayerByNit(value.tin);
                      setValue('taxpayerName', taxpayer ? taxpayer.name : '');
                      setValue('language', taxpayer ? taxpayer.locale : '');
                      clearErrors('taxpayerName');
                    }}
                    seachFuction={loadOptionsDebounced}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text readonly={true} label={taxpayerNameLabel} name="taxpayerName" />
                </Col>
                <Col>
                  <Text readonly={true} label={languageLabel} name="language" />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text label={subjectLabel} name="subject" />
                </Col>
              </Row>
              <Row>
                <Col>
                  <LexicalEditorComponent name={'content'} />
                </Col>
              </Row>
            </>
          );
        }}
      </HookFormResolver>
    </>
  );
};
